import { dark_theme, theme } from '@/theme.ts';
import { getLogger } from '@logtape/logtape';
import { MantineProvider } from '@mantine/core';
import { ConfigCatProvider, DataGovernance, PollingMode } from 'configcat-react';

import { Router } from '@/components/router.tsx';

import { featureFlagOverrides } from '@/constants/feature-flag-overrides.ts';

import { useAppVersionCheck } from '@/hooks/use-app-version-check.ts';
import useAnalytics from '@/hooks/useAnalytics.ts';
import useLoadUser from '@/hooks/useLoadUser.ts';
import usePrismicFetching from '@/hooks/usePrismicFetching.ts';
import usePrismicRef from '@/hooks/usePrismicRef.ts';

import { ColorTheme, useThemeStore } from '@/stores/themeStore.ts';

const logger = getLogger(['app']);

const configCatSdkKey = import.meta.env.VITE_CONFIG_CAT_SDK_KEY;

if (!configCatSdkKey) {
    logger.warn(
        'VITE_CONFIG_CAT_SDK_KEY is missing - rendering without ConfigCatProvider; feature flags will fall back to their default values.'
    );
}

export const App = () => {
    useAnalytics();
    useLoadUser();
    usePrismicRef();
    usePrismicFetching();
    useAppVersionCheck();
    const { theme: colorTheme } = useThemeStore();

    const appTree = (
        <MantineProvider theme={colorTheme === ColorTheme.LIGHT ? theme : dark_theme}>
            <Router />
        </MantineProvider>
    );

    if (!configCatSdkKey) {
        return appTree;
    }

    return (
        <ConfigCatProvider
            sdkKey={configCatSdkKey}
            pollingMode={PollingMode.AutoPoll}
            options={{ flagOverrides: featureFlagOverrides, dataGovernance: DataGovernance.EuOnly }}
        >
            {appTree}
        </ConfigCatProvider>
    );
};
